import { TZDate } from '@date-fns/tz';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { DeliveryWindowOutputRecord } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { CustomDateFormats, formatCurrencyAmount, getBookingTimeWindowDisplayText, noddiFormatDate } from 'noddi-util';
import { parseTimeSlotFromPublicTimes } from 'noddi-web';
import { useState } from 'react';
import { TimeWindowModalContent } from './TimeWindows/TimeWindowModal';

interface SelectedTimeWindowProps {
  selectedTimeWindow?: DeliveryWindowOutputRecord;
  /**
   * `stacked`: day over time, beside Next. `chip`: one outlined line like the other steps' selection
   * chips, so the bar keeps its height; the dialog has the delivery cost.
   */
  layout?: 'stacked' | 'chip';
}

export const SelectedTimeWindow = ({ selectedTimeWindow, layout = 'stacked' }: SelectedTimeWindowProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!selectedTimeWindow) {
    return null;
  }

  const dayText = noddiFormatDate(
    new TZDate(selectedTimeWindow.startsAt, selectedTimeWindow.timeZone.value),
    CustomDateFormats.SHORT_WEEKDAY_DAY_SHORT_MONTH
  );
  const timeText = parseTimeSlotFromPublicTimes(selectedTimeWindow);

  return (
    <>
      <NoddiDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        title={t`Selected time slot`}
      >
        <div className='flex flex-col gap-4'>
          <NoddiBasicCard className={cn('flex flex-col gap-2')}>
            <div className='flex justify-between'>
              <span className='font-semibold'>
                <Trans>Time</Trans>
              </span>
              <span> {getBookingTimeWindowDisplayText({ timeWindow: selectedTimeWindow })}</span>
            </div>
            {!!selectedTimeWindow.deliveryWindowFee?.amount && (
              <div className='flex justify-between'>
                <span className='font-semibold'>
                  <Trans>Delivery cost</Trans>
                </span>
                <span>
                  {formatCurrencyAmount({
                    money: selectedTimeWindow.deliveryWindowFee,
                    decimals: 0
                  })}
                </span>
              </div>
            )}
          </NoddiBasicCard>
          <TimeWindowModalContent />
        </div>
      </NoddiDialog>

      {layout === 'chip' ? (
        <NoddiButton
          variant='outline'
          startIcon='Calendar'
          // NoddiButton never shrinks by default; this one truncates on a narrow phone rather than pushing Next off.
          className='min-w-0 shrink'
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          {/* Time first: on a narrow phone the day truncates, never the slot itself. */}
          <span className='truncate'>
            <span className='font-medium'>{timeText}</span>{' '}
            <span className='font-normal text-muted-foreground'>{dayText}</span>
          </span>
        </NoddiButton>
      ) : (
        // Day over time, so the pick stays readable beside Next; the dialog has the delivery cost.
        <NoddiButton
          variant='ghost'
          // Two tight lines fit Next's height, so picking a time does not grow the bar.
          className='h-9 min-w-0 px-2 py-0'
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          <span className='flex flex-col items-end text-right'>
            <span className='text-xs/4 font-normal text-muted-foreground'>{dayText}</span>
            <span className='text-sm/5 font-medium'>{timeText}</span>
          </span>
        </NoddiButton>
      )}
    </>
  );
};
